import { useCallback, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { requireAuth } from "@/lib/auth";
import { useResume } from "@/components/GoogleReturn";
import { shareLink } from "@/lib/share";

export const OwnerPanel = ({ p, onChange }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decided = p.ownerStatus.startsWith("Approved") || p.ownerStatus.startsWith("Declined");

  const decide = async (decision) => {
    setBusy(true);
    try { const { data } = await api.post(`/proposals/${p.id}/decision`, { decision, note }); onChange(data); toast(data.ownerStatus); } catch (e) { toast(errMsg(e, "Something went wrong. Try again.")); }
    setBusy(false);
  };
  const invite = async () => {
    try { const { data } = await api.post(`/proposals/${p.id}/owner-invite`); await shareLink(`${window.location.origin}${data.path}`, "A proposal for your property"); onChange({ ...p, ownerStatus: p.ownerStatus === "Owner not yet invited" ? "Owner invited" : p.ownerStatus }); } catch (e) { toast(errMsg(e, "Couldn't create the invite.")); }
  };
  const doClaim = useCallback(async () => {
    try { await api.post("/owner-claims", { propertyId: p.propertyId }); const { data } = await api.get(`/proposals/${p.id}`); onChange(data); toast("Owner claim recorded (self-attested)"); } catch (e) { toast(errMsg(e, "Couldn't record the claim.")); }
  }, [p.id, p.propertyId, onChange]);
  useResume(`claim:${p.id}`, doClaim);
  const claim = () => requireAuth("Claim this property", doClaim, `claim:${p.id}`);

  return (
    <section className="rounded-xl border border-[var(--line)] bg-white/50 p-6 sm:p-8" data-testid="owner-panel">
      <div className="eyebrow mb-2">Owner</div>
      <p className="font-display text-2xl tracking-tight" data-testid="owner-status">{p.ownerStatus}</p>
      {p.multipleClaims && <p className="mt-1 text-xs text-[#B42318]" data-testid="multiple-claims">More than one person has claimed this property.</p>}
      {p.isClaimant && <p className="mt-1 text-xs text-[var(--stone-dark)]" data-testid="claim-label">{p.claimLabel}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        {p.isProposer && <button className="btn-ghost" onClick={invite} data-testid="invite-owner-button">Invite the owner</button>}
        {!p.isClaimant && !p.isProposer && <button className="btn-quiet" onClick={claim} data-testid="claim-property-button">I own or manage this property</button>}
      </div>
      {p.isClaimant && !decided && (
        <div className="mt-5 grid gap-3" data-testid="owner-decision">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note to the community (optional)" className="w-full rounded-xl border border-[var(--line)] bg-white/70 p-3 text-sm outline-none focus:border-[var(--charcoal)]" data-testid="owner-note-input" />
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" disabled={busy} onClick={() => decide("approve")} data-testid="owner-approve-button">Approve project</button>
            <button className="btn-ghost" disabled={busy} onClick={() => decide("decline")} data-testid="owner-decline-button">Decline</button>
          </div>
          <p className="text-xs text-[var(--stone-dark)]">Declining releases every conditional pledge.</p>
        </div>
      )}
      {p.audit.length > 0 && (
        <ol className="mt-6 grid gap-1 border-t border-[var(--line)] pt-4" data-testid="audit-trail">
          {p.audit.map((a, i) => (
            <li key={i} className="flex justify-between gap-3 text-xs text-[var(--stone-dark)]"><span>{a.event}{a.note ? ` — “${a.note}”` : ""}</span><span>{new Date(a.at).toLocaleDateString()}</span></li>
          ))}
        </ol>
      )}
    </section>
  );
};
